import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

export default function HomePage() {
    const [interactivesOpen, setInteractivesOpen] = useState(false);
    const [portfolioOpen, setPortfolioOpen] = useState(false);
    const [essaysOpen, setEssaysOpen] = useState(false);

    // Detect OS for conditional grid opacity
    const [isLinux, setIsLinux] = useState(false);

    useEffect(() => {
        const platform = navigator.platform.toLowerCase();
        const userAgent = navigator.userAgent.toLowerCase();
        setIsLinux(platform.includes('linux') || userAgent.includes('linux'));
    }, []);

    return (
        <div className="min-h-screen bg-black text-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 bg-black/80 backdrop-blur-sm border-b border-white/10 z-50">
                <div className="max-w-7xl mx-auto px-8 py-5 flex justify-between items-center">
                    {/* Logo/Name */}
                    <Link to="/" className="text-lg font-medium text-white hover:opacity-70 transition-opacity">
                        Wyatt Cirou
                    </Link>

                    {/* Navigation Links */}
                    <div className="flex items-center gap-1">
                        {/* Interactives Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setInteractivesOpen(!interactivesOpen)}
                                onMouseEnter={() => setInteractivesOpen(true)}
                                onMouseLeave={() => setInteractivesOpen(false)}
                                className="flex items-center gap-1 px-4 py-2 text-sm text-white/70 hover:text-white transition-colors duration-200"
                            >
                                <span>Interactives</span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${interactivesOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {interactivesOpen && (
                                <div
                                    className="absolute left-0 mt-0 w-80 bg-black border border-white/10 rounded-lg overflow-hidden shadow-2xl"
                                    onMouseEnter={() => setInteractivesOpen(true)}
                                    onMouseLeave={() => setInteractivesOpen(false)}
                                >
                                    <Link
                                        to="/zara"
                                        className="block px-5 py-4 text-white hover:bg-white/5 transition-all duration-200 border-b border-white/10 last:border-b-0"
                                    >
                                        <div className="font-medium">The Zara Interactive</div>
                                        <div className="text-sm text-white/50 mt-1">
                                            Consciousness uncertainty experiment
                                        </div>
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Project Portfolio Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setPortfolioOpen(!portfolioOpen)}
                                onMouseEnter={() => setPortfolioOpen(true)}
                                onMouseLeave={() => setPortfolioOpen(false)}
                                className="flex items-center gap-1 px-4 py-2 text-sm text-white/70 hover:text-white transition-colors duration-200"
                            >
                                <span>Project Portfolio</span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${portfolioOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {portfolioOpen && (
                                <div
                                    className="absolute left-0 mt-0 w-64 bg-black border border-white/10 rounded-lg overflow-hidden shadow-2xl"
                                    onMouseEnter={() => setPortfolioOpen(true)}
                                    onMouseLeave={() => setPortfolioOpen(false)}
                                >
                                    <div className="px-5 py-4 text-white/40">
                                        <div className="text-sm">Coming soon...</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Essays Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setEssaysOpen(!essaysOpen)}
                                onMouseEnter={() => setEssaysOpen(true)}
                                onMouseLeave={() => setEssaysOpen(false)}
                                className="flex items-center gap-1 px-4 py-2 text-sm text-white/70 hover:text-white transition-colors duration-200"
                            >
                                <span>Essays</span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${essaysOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {essaysOpen && (
                                <div
                                    className="absolute left-0 mt-0 w-64 bg-black border border-white/10 rounded-lg overflow-hidden shadow-2xl"
                                    onMouseEnter={() => setEssaysOpen(true)}
                                    onMouseLeave={() => setEssaysOpen(false)}
                                >
                                    <div className="px-5 py-4 text-white/40">
                                        <div className="text-sm">Coming soon...</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-32 min-h-screen flex items-center justify-center px-8">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                        Welcome to wcirou.dev!
                    </h1>

                    <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                        wcirou.dev is a website where I post my professional work, video interactives,
                        and essays. The dropdown menus above list all the content on the website.
                    </p>
                </div>
            </main>

            {/* Subtle Grid Background */}
            <div className={`fixed inset-0 pointer-events-none ${isLinux ? 'opacity-[0.06]' : 'opacity-[0.02]'}`}>
                <div className="absolute inset-0" style={{
                    backgroundImage: `
                        linear-gradient(to right, white 1px, transparent 1px),
                        linear-gradient(to bottom, white 1px, transparent 1px)
                    `,
                    backgroundSize: '75px 75px'
                }} />
            </div>
        </div>
    );
}